'use client';

import clsx from 'clsx';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type React from 'react';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { IconButton } from '../IconButton';
import styles from './TestimonialSlider.module.scss';

interface TestimonialSliderProps {
  heading: string;
  quotes: string[];
  headingLevel?: 1 | 2 | 3 | 4 | 5 | 6;
  headingId?: string;
}

export const TestimonialSlider: React.FC<TestimonialSliderProps> = ({
  heading,
  quotes,
  headingLevel = 2,
  headingId,
}) => {
  const t = useTranslations('components.testimonialSlider');
  const HeadingTag = `h${headingLevel}` as keyof React.JSX.IntrinsicElements;
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const slidesRef = useRef<HTMLDivElement>(null);
  const touchStartX = useRef<number | null>(null);
  const touchEndX = useRef<number | null>(null);

  const generatedId = useId();
  const sliderId = `testimonial-slider-${generatedId}`;

  const totalSlides = quotes.length;

  const goToSlide = useCallback(
    (slideIndex: number) => {
      if (isAnimating || slideIndex === currentSlide) return;

      setIsAnimating(true);
      setCurrentSlide(slideIndex);

      setTimeout(() => {
        setIsAnimating(false);
      }, 400); // Match this with CSS transition duration
    },
    [currentSlide, isAnimating],
  );

  const goToNextSlide = useCallback(() => {
    const nextSlide = (currentSlide + 1) % totalSlides;
    goToSlide(nextSlide);
  }, [currentSlide, totalSlides, goToSlide]);

  const goToPrevSlide = useCallback(() => {
    const prevSlide = (currentSlide - 1 + totalSlides) % totalSlides;
    goToSlide(prevSlide);
  }, [currentSlide, totalSlides, goToSlide]);

  // Handle touch events for swipe navigation
  const handleTouchStart = useCallback((e: TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
    touchEndX.current = null;
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    touchEndX.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!touchStartX.current || !touchEndX.current) return;

    const diffX = touchEndX.current - touchStartX.current;
    const minSwipeDistance = 50; // Minimum distance (in px) to trigger a swipe action

    if (Math.abs(diffX) > minSwipeDistance) {
      if (diffX > 0) {
        goToPrevSlide();
      } else {
        goToNextSlide();
      }
    }

    // Reset touch coordinates
    touchStartX.current = null;
    touchEndX.current = null;
  }, [goToNextSlide, goToPrevSlide]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        goToPrevSlide();
      } else if (e.key === 'ArrowRight') {
        goToNextSlide();
      }
    };

    const sliderElement = slidesRef.current;
    if (sliderElement) {
      sliderElement.addEventListener('keydown', handleKeyDown);
      sliderElement.addEventListener('touchstart', handleTouchStart, {
        passive: true,
      });
      sliderElement.addEventListener('touchmove', handleTouchMove, {
        passive: true,
      });
      sliderElement.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      if (sliderElement) {
        sliderElement.removeEventListener('keydown', handleKeyDown);
        sliderElement.removeEventListener('touchstart', handleTouchStart);
        sliderElement.removeEventListener('touchmove', handleTouchMove);
        sliderElement.removeEventListener('touchend', handleTouchEnd);
      }
    };
  }, [goToNextSlide, goToPrevSlide, handleTouchStart, handleTouchMove, handleTouchEnd]);

  return (
    <div
      className={styles.slider}
      ref={slidesRef}
      aria-roledescription="carousel"
      aria-labelledby={headingId || sliderId}
      tabIndex={-1}
    >
      <div className={styles.header}>
        <HeadingTag className={styles.heading} id={headingId || sliderId}>
          {heading}
        </HeadingTag>
        {quotes.length > 1 && (
          <div className={styles.controls}>
            <IconButton
              variant="cta"
              size="lg"
              onClick={goToPrevSlide}
              title={t('navigation.previousQuote')}
              label={t('navigation.previousQuote')}
            >
              <span className="sr-only">{t('navigation.previousQuote')}</span>
              <ArrowLeft aria-hidden="true" />
            </IconButton>
            <IconButton
              variant="cta"
              size="lg"
              onClick={goToNextSlide}
              title={t('navigation.nextQuote')}
              label={t('navigation.nextQuote')}
            >
              <span className="sr-only">{t('navigation.nextQuote')}</span>
              <ArrowRight aria-hidden="true" />
            </IconButton>
          </div>
        )}
      </div>
      <div className={styles.testimonials} role="region" aria-live="polite">
        <div
          className={styles.slidesContainer}
          style={{
            transform: `translateX(${-currentSlide * 100}%)`,
          }}
        >
          {quotes.map((quote, index) => (
            <div
              key={quote}
              className={styles.slide}
              aria-hidden={currentSlide !== index}
              aria-roledescription="slide"
              role="tabpanel"
              id={`${sliderId}-slide-${index}`}
              aria-labelledby={`${sliderId}-tab-${index}`}
            >
              <p className={styles.quote}>{quote}</p>
            </div>
          ))}
        </div>
      </div>
      {quotes.length > 1 && (
        <div className={styles.indicator} role="tablist" aria-label={t('indicator.selectSlide')}>
          {quotes.map((_, index) => (
            <button
              key={index}
              className={clsx(styles.indicatorDot, {
                [styles.indicatorDotActive]: index === currentSlide,
              })}
              onClick={() => goToSlide(index)}
              aria-selected={index === currentSlide}
              aria-controls={`${sliderId}-slide-${index}`}
              id={`${sliderId}-tab-${index}`}
              role="tab"
              aria-label={t('indicator.showTestimonial', { index: index + 1, total: totalSlides })}
              disabled={isAnimating}
            >
              <span className="sr-only">{t('indicator.quote', { index: index + 1 })}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
